import { Button, Classes, Dialog, Icon, Tabs, Tab } from '@blueprintjs/core'
import React, { FC } from 'react'
// import BotAnalytics from './BotAnalytics'
import BotRegisterScreenCustomization from './BotRegisterScreenCustomization'
import ChatInterfaceCustomization from './ChatInterfaceCustomization'
import WebchatIconCustomization from './WebchatIconCustomization'
import '../../user/modern-settings.css'

interface BotDetails {
  website?: string
  phoneNumber?: string
  termsConditions?: string
  privacyPolicy?: string
  emailAddress?: string
  avatarUrl?: string
  coverPictureUrl?: string
}

interface BotInfo {
  locked: boolean
  disabled: boolean
  private: boolean
  details: BotDetails
  description?: string
  active: boolean
  version: string
  author?: string
  license?: string
  imports: {
    modules?: string[]
    incomingMiddleware?: string[]
    outgoingMiddleware?: string[]
    contentTypes?: string[]
  }
  dialog?: {
    timeoutInterval: string
  }
  logs?: {
    expiration: string
  }
  defaultLanguage: string
  languages: string[]
  id: string
  name: string
  owner?: string
  pipeline_status?: {
    current_stage: {
      id: string
      promoted_on: string
      promoted_by: string
    }
  }
  messaging?: {
    channels?: {
      [channelName: string]: any
    }
  }
  telegramToken?: string
  slackBotToken?: string
  messengerAccessToken?: string
  twilioAccountSid?: string
}

interface Props {
  bot: BotInfo | null
  isOpen: boolean
  onClose: () => void
  getActiveChannels: (bot: BotInfo) => string[]
  getChannelIcon: (channel: string) => string
  getChannelColor: (channel: string) => string
  getBotAvatar: (bot: BotInfo) => string
  getStatusColor: (bot: BotInfo) => string
  getStatusText: (bot: BotInfo) => string
}

const BotDetailsDialog: FC<Props> = ({
  bot,
  isOpen,
  onClose,
  getActiveChannels,
  getChannelIcon,
  getChannelColor,
  getBotAvatar,
  getStatusColor,
  getStatusText
}) => {
  if (!bot) {
    return null
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      transitionDuration={0}
      className="modern-dialog"
      style={{ width: '1300px', maxWidth: '95vw', height: '94vh', maxHeight: '94vh', display: 'flex', flexDirection: 'column', paddingBottom: 0 }}
    >
      <div className={Classes.DIALOG_BODY} style={{ padding: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, margin: 0 }}>
        {/* Compact Header */}
        <div style={{
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#ffffff'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            overflow: 'hidden',
            marginRight: '10px',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #cbd5e1',
            flexShrink: 0
          }}>
            <img
              src={getBotAvatar(bot)}
              alt={bot.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(bot.name)}&background=5C7CFA&color=fff&size=128`
              }}
            />
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
              {bot.name}
            </h3>
            <div style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '10px',
              backgroundColor: bot.disabled ? '#fee2e2' : (!bot.active ? '#ffedd5' : '#d1fae5'),
              color: bot.disabled ? '#991b1b' : (!bot.active ? '#c2410c' : '#065f46'),
              fontSize: '10px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {getStatusText(bot)}
            </div>
          </div>
          <Button className="modern-btn-secondary modern-btn-icon" icon="cross" onClick={onClose} style={{ marginLeft: 'auto', width: '28px', height: '28px', minHeight: '28px', minWidth: '28px', padding: 0 }} />
        </div>

        {/* Tabs Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <style>{`
            .bot-details-tabs {
              display: flex !important;
              flex-direction: column !important;
              flex: 1 !important;
              height: 100% !important;
              min-height: 0 !important;
              overflow: hidden !important;
            }
            .bot-details-tabs .bp3-tab-list {
              background: #ffffff;
              border-bottom: 2px solid #e2e8f0;
              padding: 0 20px;
              display: flex;
              gap: 12px;
              flex-shrink: 0 !important;
            }
            .bot-details-tabs .bp3-tab {
              padding: 10px 4px;
              font-size: 13px;
              font-weight: 600;
              color: #64748b;
              border-radius: 0;
              transition: all 0.2s ease;
              border: none;
              border-bottom: 2px solid transparent;
              margin-bottom: -2px;
              position: relative;
              cursor: pointer;
            }
            .bot-details-tabs .bp3-tab:hover {
              background-color: transparent;
              color: #0f172a;
            }
            .bot-details-tabs .bp3-tab[aria-selected="true"] {
              background: transparent;
              color: #04a9e1;
              font-weight: 600;
              border-bottom: 2px solid #04a9e1;
            }
            .bot-details-tabs .bp3-tab[aria-selected="true"]:after {
              display: none;
            }
            .bot-details-tabs .bp3-tab-indicator-wrapper {
              display: none !important;
            }
            .bot-details-tabs .bp3-tab-panel {
              margin: 0 !important;
              flex: 1 !important;
              display: flex !important;
              flex-direction: column !important;
              min-height: 0 !important;
              overflow: hidden !important;
            }
          `}</style>
          <Tabs
            id="bot-details-tabs"
            renderActiveTabPanelOnly
            className="bot-details-tabs"
          >
            <Tab
              id="info"
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="info-sign" iconSize={15} />
                  Information
                </span>
              }
              panel={
                <div className="modern-scrollbar" style={{ padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f8fafc' }}>
                  {/* Compact Two-Column Layout */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
                    {/* Left Column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Core Information Card */}
                      <div className="modern-card" style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
                          <Icon icon="info-sign" iconSize={16} color="#04a9e1" />
                          <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Core Information
                          </h4>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Bot ID</span>
                            <span style={{ fontSize: '12px', color: '#0f172a', fontWeight: 600, fontFamily: 'monospace', maxWidth: '60%', textAlign: 'right', wordBreak: 'break-all' }}>{bot.id}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Version</span>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#0369a1', backgroundColor: '#e0f2fe', padding: '2px 8px', borderRadius: '12px' }}>v{bot.version}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Owner</span>
                            <span style={{ fontSize: '12px', color: '#0f172a', fontWeight: 600 }}>{bot.owner || 'N/A'}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Author</span>
                            <span style={{ fontSize: '12px', color: '#0f172a', fontWeight: 600 }}>xMati</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Status</span>
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              color: bot.disabled ? '#991b1b' : (!bot.active ? '#c2410c' : '#065f46'),
                              backgroundColor: bot.disabled ? '#fee2e2' : (!bot.active ? '#ffedd5' : '#d1fae5'),
                              padding: '2px 8px',
                              borderRadius: '12px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              {getStatusText(bot)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Language Configuration Card */}
                      <div className="modern-card" style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
                          <Icon icon="translate" iconSize={16} color="#04a9e1" />
                          <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Language Configuration
                          </h4>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Default</span>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#0369a1', backgroundColor: '#e0f2fe', padding: '2px 8px', borderRadius: '12px' }}>{bot.defaultLanguage?.toUpperCase() || 'N/A'}</span>
                          </div>
                          <div style={{ padding: '8px 0' }}>
                            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, display: 'block', marginBottom: '8px' }}>Supported Languages</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {bot.languages && bot.languages.length > 0 ? (
                                bot.languages.map((lang, idx) => (
                                  <span key={idx} style={{ fontSize: '11px', fontWeight: 600, color: '#475569', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: '8px' }}>{lang.toUpperCase()}</span>
                                ))
                              ) : (
                                <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>None configured</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Description Card */}
                      <div className="modern-card" style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
                          <Icon icon="document" iconSize={16} color="#04a9e1" />
                          <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Bot Description
                          </h4>
                        </div>
                        <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
                          {bot.description || 'No description available for this bot.'}
                        </p>
                      </div>

                      {/* Active Channels Card */}
                      <div className="modern-card" style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
                          <Icon icon="globe-network" iconSize={16} color="#04a9e1" />
                          <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Active Channels ({getActiveChannels(bot).length})
                          </h4>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {getActiveChannels(bot).length > 0 ? (
                            getActiveChannels(bot).map((channel, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '4px 10px',
                                  borderRadius: '8px',
                                  backgroundColor: '#f1f5f9',
                                  border: '1px solid #e2e8f0',
                                  color: '#475569',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  gap: '6px'
                                }}
                              >
                                <Icon icon={getChannelIcon(channel) as any} iconSize={12} color="#64748b" />
                                <span style={{ textTransform: 'capitalize' }}>{channel}</span>
                              </div>
                            ))
                          ) : (
                            <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                              No channels configured
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Contact Details Card */}
                      {bot.details && (bot.details.website || bot.details.emailAddress || bot.details.phoneNumber) && (
                        <div className="modern-card" style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
                            <Icon icon="phone" iconSize={16} color="#04a9e1" />
                            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Contact Details
                            </h4>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {bot.details.website && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <Icon icon="globe" iconSize={14} color="#64748b" />
                                <a href={bot.details.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#04a9e1', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                                  {bot.details.website}
                                </a>
                              </div>
                            )}
                            {bot.details.emailAddress && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <Icon icon="envelope" iconSize={14} color="#64748b" />
                                <a href={`mailto:${bot.details.emailAddress}`} style={{ fontSize: '12px', color: '#04a9e1', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                                  {bot.details.emailAddress}
                                </a>
                              </div>
                            )}
                            {bot.details.phoneNumber && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                                <Icon icon="phone" iconSize={14} color="#64748b" />
                                <a href={`tel:${bot.details.phoneNumber}`} style={{ fontSize: '12px', color: '#04a9e1', textDecoration: 'none', flex: 1, fontWeight: 500 }}>
                                  {bot.details.phoneNumber}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Pipeline Status Card */}
                      {bot.pipeline_status && (
                        <div className="modern-card" style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
                            <Icon icon="git-branch" iconSize={16} color="#04a9e1" />
                            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Pipeline Status
                            </h4>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div style={{ padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                <Icon icon="user" iconSize={12} color="#64748b" />
                                <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Created By</span>
                              </div>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                                {bot.owner || bot.pipeline_status.current_stage.promoted_by}
                              </div>
                            </div>
                            <div style={{ padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                <Icon icon="calendar" iconSize={12} color="#64748b" />
                                <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Created On</span>
                              </div>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                                {new Date(bot.pipeline_status.current_stage.promoted_on).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              }
            />
            {/*
            <Tab
              id="analytics"
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="chart" iconSize={15} />
                  Analytics
                </span>
              }
              panel={
                <div className="modern-scrollbar" style={{ overflowY: 'auto', flex: 1, backgroundColor: '#f8fafc' }}>
                  <BotAnalytics bot={bot} />
                </div>
              }
            />
            */}
            <Tab
              id="webchat-icon"
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="widget" iconSize={15} />
                  Webchat Icon
                </span>
              }
              panel={
                <div className="modern-scrollbar" style={{ overflowY: 'auto', flex: 1 }}>
                  <WebchatIconCustomization botId={bot.id} />
                </div>
              }
            />
            <Tab
              id="chat-interface"
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="application" iconSize={15} />
                  Chat Interface
                </span>
              }
              panel={
                <div className="modern-scrollbar" style={{ overflowY: 'auto', flex: 1 }}>
                  <ChatInterfaceCustomization botId={bot.id} />
                </div>
              }
            />
            <Tab
              id="bot-register-screen"
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="form" iconSize={15} />
                  Bot Register Screen
                </span>
              }
              panel={
                <div className="modern-scrollbar" style={{ overflowY: 'auto', flex: 1 }}>
                  <BotRegisterScreenCustomization botId={bot.id} />
                </div>
              }
            />

          </Tabs>
        </div>
      </div>
    </Dialog>
  )
}

export default BotDetailsDialog
